import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgbDropdownModule, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs';
import { BasePageComponent } from '../../../_shared/components/page/base-page.component';
import { AccountFilterComponent } from '../../../_shared/components/filters/account-filter/account-filter.component';
import { DateFilterComponent } from '../../../_shared/components/filters/date-filter/date-filter.component';
import { AccountFilterData, DateIntervalFilterData, WidgetFilterData } from '../../../_shared/models';
import { DateInterval } from '../../../_shared/enums';
import { FilterService } from '../../../_shared/services/filter.service';
import { ErrorHandlerService } from '../../../_shared/services/error-handler.service';
import { isEmptyObject } from '../../../_shared/helpers/object.helper';
import { buildDateFilterFromPageFilter } from '../../../_shared/helpers/page-filter-state-helper';
import { InMaintenanceWidgetComponent } from './components/widgets/in-maintenance-widget/in-maintenance-widget.component';
import { ReactiveMaintenanceWidgetComponent } from './components/widgets/reactive-maintenance-widget/reactive-maintenance-widget.component';
import { PreventiveMaintenanceWidgetComponent } from './components/widgets/preventive-maintenance-widget/preventive-maintenance-widget.component';
import { OverdueWidgetComponent } from './components/widgets/overdue-widget/overdue-widget.component';
import { MaintenanceOverviewAllTableComponent } from './components/maintenance-overview-all-table/maintenance-overview-all-table.component';
import { MaintenanceRequestsTableComponent } from './components/maintenance-requests-table/maintenance-requests-table.component';
import { UpcomingMaintenancesTableComponent } from './components/upcoming-maintenances-table/upcoming-maintenances-table.component';
import { OverdueMaintenancesTableComponent } from './components/overdue-maintenances-table/overdue-maintenances-table.component';
import { CompletedMaintenanceOverviewComponent } from './components/completed-maintenance-overview/completed-maintenance-overview.component';
import { MaintenanceOverviewService } from './services/maintenance-overview.service';
import { MaintenanceTab } from './enums/maintenance-overview.enum';
import { MaintenanceStatus } from '../../_shared/enums/maintenance-status.enum';
import { MaintenanceDetailsResponse, MaintenanceRequestDetailsResponse, OverdueMaintenanceDetailsResponse } from './models/maintenance-overview-response.model';
import { MaintenanceOverviewRequest, UpdateMaintenanceStatusRequest } from './models/maintenance-overview-request.model';
import { CreateMaintenanceModalComponent } from '../../_shared/components/modals/create-maintenance-modal/create-maintenance-modal.component';
import { CompletedMaintenanceDetailsResponse } from './models/maintenance-overview-response.model';
import { getSelectedFilterLabel } from '../../../_shared/helpers/kpiWidget/selected-filter.helper';
import { MaintenanceRequestAction } from './models/maintenance-overview.model';
import { MaintenanceModel } from '../../_shared/enums/maintenance-model.enum';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-maintenance-overview',
  standalone: true,
  imports: [
    CommonModule,
    NgbDropdownModule,
    AccountFilterComponent,
    DateFilterComponent,
    InMaintenanceWidgetComponent,
    ReactiveMaintenanceWidgetComponent,
    PreventiveMaintenanceWidgetComponent,
    OverdueWidgetComponent,
    MaintenanceOverviewAllTableComponent,
    MaintenanceRequestsTableComponent,
    UpcomingMaintenancesTableComponent,
    OverdueMaintenancesTableComponent,
    CompletedMaintenanceOverviewComponent,
    TranslocoDirective
  ],
  templateUrl: './maintenance-overview.component.html',
  styleUrl: './maintenance-overview.component.scss'
})
export class MaintenanceOverviewComponent extends BasePageComponent implements OnInit {
  private transloco = inject(TranslocoService);
  private maintenanceService = inject(MaintenanceOverviewService);
  private filterService = inject(FilterService);
  private errorService = inject(ErrorHandlerService);
  private ngbModal = inject(NgbModal);

  // Filter state
  accountFilter: AccountFilterData = {} as AccountFilterData;
  dateFilter: DateIntervalFilterData = {} as DateIntervalFilterData;
  defaultInterval: DateInterval = DateInterval.Month;
  disabledIntervals: DateInterval[] = [DateInterval.Half];

  // Maintenance model filter
  maintenanceModel: MaintenanceModel | null = null;
  maintenanceModelFilter = MaintenanceModel;
  maintenanceModelLabels: Record<number, string> = {
    [MaintenanceModel.InHouse]: this.transloco.translate('maintenanceOverview.page.inHouse'),
    [MaintenanceModel.Contract]: this.transloco.translate('maintenanceOverview.page.contract')
  };

  // Tab state
  activeTab: MaintenanceTab = MaintenanceTab.All;
  maintenanceTab = MaintenanceTab;

  // all maintenances state
  maintenanceData: MaintenanceDetailsResponse[] = [];
  hasError: boolean = false;
  isLoading: boolean = true;
  page: number = 1;
  totalPages: number = 0;
  totalCount: number = 0;
  searchTerm: string = '';

  // maintenance requests state
  maintenanceRequests: MaintenanceRequestDetailsResponse[] = [];
  hasRequestsError: boolean = false;
  isRequestsLoading: boolean = true;
  requestsPage: number = 1;
  requestsTotalPages: number = 0;
  requestsTotalCount: number = 0;
  requestsSearchTerm: string = '';
  requestsSelectionResetToken: number = 0;

  // Upcoming maintenances state
  upcomingMaintenances: MaintenanceDetailsResponse[] = [];
  hasUpcomingError: boolean = false;
  isUpcomingLoading: boolean = true;
  upcomingPage: number = 1;
  upcomingTotalCount: number = 0;
  upcomingTotalPages: number = 0;
  upcomingSearchTerm: string = '';

  // Overdue maintenances state
  overdueMaintenances: OverdueMaintenanceDetailsResponse[] = [];
  hasOverdueError: boolean = false;
  isOverdueLoading: boolean = true;
  overduePage: number = 1;
  overdueTotalPages: number = 1;
  overdueTotalCount: number = 0;
  overdueSearchTerm: string = '';

  // Completed maintenances state
  completedMaintenances: CompletedMaintenanceDetailsResponse[] = [];
  hasCompletedError: boolean = false;
  isCompletedLoading: boolean = true;
  completedPage: number = 1;
  completedTotalPages: number = 1;
  completedTotalCount: number = 0;
  completedSearchTerm: string = '';

  pageSize: number = 6;
  maintenanceStatus = MaintenanceStatus;

  private cancelMaintenanceData = new Subject<void>();
  private cancelRequests = new Subject<void>();
  private cancelUpcoming = new Subject<void>();
  private cancelOverdue = new Subject<void>();
  private cancelCompleted = new Subject<void>();

  private hasAccountFilterEmitted = false;
  private hasDateFilterEmitted = false;

  constructor() {
    super();
  }

  override ngOnInit(): void {
    super.ngOnInit();
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.cancelMaintenanceData.next();
    this.cancelMaintenanceData.complete();
    this.cancelRequests.next();
    this.cancelRequests.complete();
    this.cancelUpcoming.next();
    this.cancelUpcoming.complete();
    this.cancelOverdue.next();
    this.cancelOverdue.complete();
    this.cancelCompleted.next();
    this.cancelCompleted.complete();
  }

  isTabActive(tab: MaintenanceTab): boolean {
    return this.activeTab === tab;
  }

  onTabChange(tab: MaintenanceTab): void {
    if (this.activeTab !== tab) {
      this.activeTab = tab;
      this.page = 1;
      this.resetAllSearchTerms();

      this.updatePageFilterState();
      this.loadData();
    }
  }

  onMaintenanceCalendarClick(): void {
    this.router.navigate(['/asset-management/maintenance-overview/calendar']);
  }

  onCreateMaintenanceClick(): void {
    const modalRef = this.ngbModal.open(CreateMaintenanceModalComponent, {
      size: 'md',
      centered: true,
      backdrop: 'static',
      container: 'body'
    });
    modalRef.componentInstance.accountIds = this.accountFilter.accountIds ?? [];

    modalRef.closed.subscribe(() => {
      this.loadData();
    });
  }

  onAccountFilterChange(filter: AccountFilterData): void {
    this.accountFilter = filter;
    this.hasAccountFilterEmitted = true;
    this.requestsSelectionResetToken++;
    this.updateFilter();
  }

  onDateFilterChange(filter: DateIntervalFilterData): void {
    this.dateFilter = filter;
    this.hasDateFilterEmitted = true;
    this.requestsSelectionResetToken++;
    this.updateFilter();
  }

  updateFilter(): void {
    if (!this.hasAccountFilterEmitted || !this.hasDateFilterEmitted) {
      return;
    }

    if (!isEmptyObject(this.dateFilter) && !isEmptyObject(this.accountFilter)) {
      this.filterService.updateFilter({
        ...this.dateFilter,
        ...this.accountFilter
      } as WidgetFilterData);

      this.updatePageFilterState();

      // Load data based on active tab
      this.loadData();
    }
  }

  getSelectedFilterLabel(filter: DateIntervalFilterData): string {
    return getSelectedFilterLabel(filter, (key, params) => this.transloco.translate(key, params), this.transloco.getActiveLang());
  }

  get maintenanceModelLabel(): string {
    if (this.maintenanceModel === null) return this.transloco.translate('maintenanceOverview.page.maintenanceModel');
    return this.maintenanceModelLabels[this.maintenanceModel];
  }

  onMaintenanceModelFilter(model: MaintenanceModel | null): void {
    this.maintenanceModel = model;
    this.page = 1;
    this.requestsSelectionResetToken++;

    this.loadData();
  }

  loadData(): void {
    if (this.activeTab === MaintenanceTab.All) {
      this.loadMaintenanceData();
    } else if (this.activeTab === MaintenanceTab.MaintenanceRequests) {
      this.loadMaintenanceRequests();
    } else if (this.activeTab === MaintenanceTab.UpcomingMaintenances) {
      this.loadUpcomingMaintenances();
    } else if (this.activeTab === MaintenanceTab.OverdueMaintenances) {
      this.loadOverdueMaintenances();
    } else if (this.activeTab === MaintenanceTab.CompletedMaintenances) {
      this.loadCompletedMaintenances();
    }
  }

  // All Maintenances methods
  onSearchTermUpdate(searchTerm: string): void {
    this.searchTerm = searchTerm;
    this.page = 1;
    this.updatePageFilterState();

    this.loadData();
  }

  async loadMaintenanceData(): Promise<void> {
    this.hasError = false;
    this.isLoading = true;

    try {
      const request: MaintenanceOverviewRequest = {
        accountIds: this.accountFilter.accountIds,
        timeFrom: this.dateFilter.startDate!,
        timeTo: this.dateFilter.endDate!,
        page: this.page,
        pageSize: this.pageSize,
        searchTerm: this.searchTerm,
        maintenanceModel: this.maintenanceModel,
        maintenanceStatuses: [MaintenanceStatus.Approved, MaintenanceStatus.Overdue, MaintenanceStatus.Complete]
      };

      this.cancelMaintenanceData.next();
      const response = await this.maintenanceService.getMaintenanceDetails(request, this.cancelMaintenanceData);

      this.totalCount = response.data?.totalCount ?? 0;
      this.totalPages = response.data?.totalPages ?? 0;
      this.maintenanceData = response.data?.results.map(item => {
        return {
          id: item.id,
          assetId: item.assetId,
          assetName: item.assetName,
          isAutoTrigger: item.isAutoTrigger,
          maintenanceModel: item.maintenanceModel,
          maintenanceSource: item.maintenanceSource,
          rangeLeft: item.rangeLeft,
          distanceOver: item.distanceOver,
          maintenanceReason: item.maintenanceReason,
          maintenanceType: item.maintenanceType,
          assignedTo: item.assignedTo,
          cost: item.cost,
          dueDate: item.dueDate,
          maintenanceStatus: item.maintenanceStatus,
          createdAt: item.createdAt
        }
      }) ?? [];

      if (this.maintenanceData.length === 0 && this.page > 1) {
        this.page = 1;
        await this.loadMaintenanceData();
        return;
      }

      this.isLoading = false;
    } catch (error) {
      this.errorService.handleError(error, {
        callback: () => {
          this.hasError = true;
          this.isLoading = false;
        }
      });
    }
  }

  onAllPageChange(page: number): void {
    this.page = page;
    this.loadData();
  }

  onExportClick(): void { }
  onRowActionClick(item: MaintenanceDetailsResponse): void { }

  // Maintenance Requests methods
  onRequestsSearchTermUpdate(searchTerm: string): void {
    this.requestsSearchTerm = searchTerm;
    this.requestsPage = 1;
    this.requestsSelectionResetToken++;
    this.updatePageFilterState();

    this.loadData();
  }

  async loadMaintenanceRequests(): Promise<void> {
    this.hasRequestsError = false;
    this.isRequestsLoading = true;

    try {
      const request: MaintenanceOverviewRequest = {
        accountIds: this.accountFilter.accountIds,
        timeFrom: this.dateFilter.startDate!,
        timeTo: this.dateFilter.endDate!,
        page: this.requestsPage,
        pageSize: this.pageSize,
        searchTerm: this.requestsSearchTerm,
        maintenanceModel: this.maintenanceModel,
        maintenanceStatuses: [MaintenanceStatus.Pending, MaintenanceStatus.Rejected]
      };

      this.cancelRequests.next();
      const response = await this.maintenanceService.getMaintenanceDetails(request, this.cancelRequests);

      this.requestsTotalCount = response.data?.totalCount ?? 0;
      this.requestsTotalPages = response.data?.totalPages ?? 0;
      this.maintenanceRequests = response.data?.results.map(item => {
        return {
          id: item.id,
          assetId: item.assetId,
          assetName: item.assetName,
          isAutoTrigger: item.isAutoTrigger,
          maintenanceModel: item.maintenanceModel,
          maintenanceSource: item.maintenanceSource,
          rangeLeft: item.rangeLeft,
          maintenanceReason: item.maintenanceReason,
          maintenanceType: item.maintenanceType,
          cost: item.cost,
          maintenanceStatus: item.maintenanceStatus,
          createdAt: item.createdAt
        }
      }) ?? [];

      if (this.maintenanceRequests.length === 0 && this.requestsPage > 1) {
        this.requestsPage = 1;
        await this.loadMaintenanceRequests();
        return;
      }

      this.isRequestsLoading = false;
    } catch (error) {
      this.errorService.handleError(error, {
        callback: () => {
          this.hasRequestsError = true;
          this.isRequestsLoading = false;
        }
      });
    }
  }

  async onApproveRequest(action: MaintenanceRequestAction): Promise<void> {
    try {
      const request: UpdateMaintenanceStatusRequest = {
        maintenanceId: action.item.id,
        assetId: action.item.assetId,
        maintenanceStatus: MaintenanceStatus.Approved
      };

      await this.maintenanceService.updateMaintenanceStatus([request]);
      action.onComplete(true);
      await this.loadMaintenanceRequests();
    } catch (error) {
      this.errorService.handleError(error, { showToast: false });
      action.onComplete(false);
    }
  }

  async onBulkApproveRequests(items: MaintenanceRequestDetailsResponse[]): Promise<void> {
    try {
      const requests: UpdateMaintenanceStatusRequest[] = items.map(item => {
        return {
          maintenanceId: item.id,
          assetId: item.assetId,
          maintenanceStatus: MaintenanceStatus.Approved
        };
      });

      await this.maintenanceService.updateMaintenanceStatus(requests);

      this.requestsPage = 1;
      this.requestsSelectionResetToken++;
      await this.loadMaintenanceRequests();
    } catch (error) {
      this.errorService.handleError(error);
    }
  }

  async onRejectRequest(action: MaintenanceRequestAction): Promise<void> {
    try {
      const request: UpdateMaintenanceStatusRequest = {
        maintenanceId: action.item.id,
        assetId: action.item.assetId,
        maintenanceStatus: action.item.maintenanceStatus === MaintenanceStatus.Rejected ? MaintenanceStatus.Pending : MaintenanceStatus.Rejected
      };

      await this.maintenanceService.updateMaintenanceStatus([request]);
      action.onComplete(true);
      await this.loadMaintenanceRequests();
    } catch (error) {
      this.errorService.handleError(error, { showToast: false });
      action.onComplete(false);
    }
  }

  onRequestsPageChange(page: number): void {
    this.requestsPage = page;
    this.loadData();
  }

  // Upcoming Maintenances methods
  onUpcomingSearchTermUpdate(searchTerm: string): void {
    this.upcomingSearchTerm = searchTerm;
    this.upcomingPage = 1;
    this.updatePageFilterState();

    this.loadData();
  }

  async loadUpcomingMaintenances(): Promise<void> {
    this.hasUpcomingError = false;
    this.isUpcomingLoading = true;

    try {
      const request: MaintenanceOverviewRequest = {
        accountIds: this.accountFilter.accountIds,
        timeFrom: this.dateFilter.startDate!,
        timeTo: this.dateFilter.endDate!,
        page: this.upcomingPage,
        pageSize: this.pageSize,
        searchTerm: this.upcomingSearchTerm,
        maintenanceModel: this.maintenanceModel,
        maintenanceStatuses: [MaintenanceStatus.Approved]
      };

      this.cancelUpcoming.next();
      const response = await this.maintenanceService.getMaintenanceDetails(request, this.cancelUpcoming);

      this.upcomingTotalCount = response.data?.totalCount ?? 0;
      this.upcomingTotalPages = response.data?.totalPages ?? 0;
      this.upcomingMaintenances = response.data?.results.map(item => {
        return {
          id: item.id,
          assetId: item.assetId,
          assetName: item.assetName,
          maintenanceModel: item.maintenanceModel,
          maintenanceSource: item.maintenanceSource,
          rangeLeft: item.rangeLeft,
          distanceOver: item.distanceOver,
          maintenanceReason: item.maintenanceReason,
          maintenanceType: item.maintenanceType,
          isAutoTrigger: item.isAutoTrigger,
          assignedTo: item.assignedTo,
          cost: item.cost,
          dueDate: item.dueDate,
          maintenanceStatus: item.maintenanceStatus,
          createdAt: item.createdAt
        }
      }) ?? [];

      if (this.upcomingMaintenances.length === 0 && this.upcomingPage > 1) {
        this.upcomingPage = 1;
        await this.loadUpcomingMaintenances();
        return;
      }

      this.isUpcomingLoading = false;
    } catch (error) {
      this.errorService.handleError(error, {
        callback: () => {
          this.hasUpcomingError = true;
          this.isUpcomingLoading = false;
        }
      });
    }
  }

  onUpcomingPageChange(page: number): void {
    this.upcomingPage = page;
    this.loadData();
  }

  // Overdue Maintenances methods
  onOverdueSearchTermUpdate(searchTerm: string): void {
    this.overdueSearchTerm = searchTerm;
    this.overduePage = 1;
    this.updatePageFilterState();

    this.loadData();
  }

  async loadOverdueMaintenances(): Promise<void> {
    this.hasOverdueError = false;
    this.isOverdueLoading = true;

    try {
      const request: MaintenanceOverviewRequest = {
        accountIds: this.accountFilter.accountIds,
        timeFrom: this.dateFilter.startDate!,
        timeTo: this.dateFilter.endDate!,
        page: this.overduePage,
        pageSize: this.pageSize,
        searchTerm: this.overdueSearchTerm,
        maintenanceModel: this.maintenanceModel,
        maintenanceStatuses: [MaintenanceStatus.Overdue]
      };

      this.cancelOverdue.next();
      const response = await this.maintenanceService.getMaintenanceDetails(request, this.cancelOverdue);

      this.overdueTotalCount = response.data?.totalCount ?? 0;
      this.overdueTotalPages = response.data?.totalPages ?? 0;
      this.overdueMaintenances = response.data?.results.map(item => {
        return {
          id: item.id,
          assetId: item.assetId,
          assetName: item.assetName,
          isAutoTrigger: item.isAutoTrigger,
          maintenanceSource: item.maintenanceSource,
          rangeLeft: item.rangeLeft,
          distanceOver: item.distanceOver,
          maintenanceReason: item.maintenanceReason,
          maintenanceType: item.maintenanceType,
          assignedTo: item.assignedTo,
          cost: item.cost,
          dueDate: item.dueDate,
          maintenanceStatus: item.maintenanceStatus,
          createdAt: item.createdAt
        }
      }) ?? [];

      if (this.overdueMaintenances.length === 0 && this.overduePage > 1) {
        this.overduePage = 1;
        await this.loadOverdueMaintenances();
        return;
      }

      this.isOverdueLoading = false;
    } catch (error) {
      this.errorService.handleError(error, {
        callback: () => {
          this.hasOverdueError = true;
          this.isOverdueLoading = false;
        }
      });
    }
  }

  onOverduePageChange(page: number): void {
    this.overduePage = page;
    this.loadData();
  }

  // Completed maintenances methods
  onCompletedSearchTermUpdate(searchTerm: string): void {
    this.completedSearchTerm = searchTerm;
    this.completedPage = 1;
    this.updatePageFilterState();

    this.loadData();
  }
  
  async loadCompletedMaintenances(): Promise<void> {
    this.hasCompletedError = false;
    this.isCompletedLoading = true;

    try {
      const request: MaintenanceOverviewRequest = {
        accountIds: this.accountFilter.accountIds,
        timeFrom: this.dateFilter.startDate!,
        timeTo: this.dateFilter.endDate!,
        page: this.completedPage,
        pageSize: this.pageSize,
        searchTerm: this.completedSearchTerm,
        maintenanceModel: this.maintenanceModel,
        maintenanceStatuses: [MaintenanceStatus.Complete]
      };

      this.cancelCompleted.next();
      const response = await this.maintenanceService.getMaintenanceDetails(request, this.cancelCompleted);

      this.completedTotalCount = response.data?.totalCount ?? 0;
      this.completedTotalPages = response.data?.totalPages ?? 0;
      this.completedMaintenances = response.data?.results.map(item => {
        return {
          id: item.id,
          assetName: item.assetName,
          isAutoTrigger: item.isAutoTrigger,
          maintenanceSource: item.maintenanceSource,
          maintenanceReason: item.maintenanceReason,
          maintenanceType: item.maintenanceType,
          cost: item.cost,
          maintenanceStatus: item.maintenanceStatus,
          createdAt: item.createdAt,
          completedAt: item.completedAt ?? new Date(),
          downtime: item.downtime ?? 0
        }
      }) ?? [];

      if (this.completedMaintenances.length === 0 && this.completedPage > 1) {
        this.completedPage = 1;
        await this.loadCompletedMaintenances();
        return;
      }

      this.isCompletedLoading = false;
    } catch (error) {
      this.errorService.handleError(error, {
        callback: () => {
          this.hasCompletedError = true;
          this.isCompletedLoading = false;
        }
      });
    }
  }

  onCompletedPageChange(page: number): void {
    this.completedPage = page;
    this.loadData();
  }

  private resetAllSearchTerms(): void {
    this.searchTerm = '';
    this.requestsSearchTerm = '';
    this.upcomingSearchTerm = '';
    this.overdueSearchTerm = '';
    this.completedSearchTerm = '';
  }

  protected override onRefresh(): void { }

  protected override shouldRefresh(): boolean { return false; }

  protected override getFilters(): any {
    return {
      ...this.dateFilter,
      ...this.accountFilter,
      searchTerm: this.searchTerm,
      maintenanceModel: this.maintenanceModel,
      activeTab: this.activeTab
    };
  }

  protected override applyPageFilterState(pageFilter: any): void {
    this.accountFilter = { accountIds: pageFilter.accountIds ?? [] } as AccountFilterData;

    if (pageFilter.interval !== undefined || pageFilter.isCustomDate !== undefined) {
      this.dateFilter = buildDateFilterFromPageFilter(this.dateFilter, pageFilter);
    }

    this.searchTerm = pageFilter.searchTerm ?? '';
    this.maintenanceModel = pageFilter.maintenanceModel ?? null;

    const persistedTab = pageFilter.activeTab as MaintenanceTab | undefined;
    if (persistedTab && Object.values(MaintenanceTab).includes(persistedTab)) {
      this.activeTab = persistedTab;
    }
  }
}